import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isNotEmpty } from '@newfront-insurance/core';
import { cn, Textarea } from '@newfront-insurance/core-ui/v2';
import type { FileAttachment } from '@newfront-insurance/nova-core';
import { useCallback, useEffect } from 'react';

import { useChatInputContext, useDragHandlersRef } from './chat-input-context';
import { SlashCommandMenu, useSlashCommandMenu } from './slash-command-menu';
import { useSlashCommands } from '../../../../hooks/use-slash-commands';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatInputTextareaProps {
  placeholder?: string;
  className?: string;
}

/**
 * Textarea component for chat input.
 * Handles text input, auto-height adjustment, and keyboard shortcuts.
 * Uses context to access input state and submit handler.
 */
export function ChatInputTextarea({ placeholder = 'How can I help you today?', className }: ChatInputTextareaProps) {
  const { input, setInput, textareaRef, handleSubmit, status } = useChatInputContext();
  const { additionalContext, accountUuid, userUuid } = useChatOverlayContext();
  const handlersRef = useDragHandlersRef();
  const { handlePaste } = handlersRef.current;

  const attachments = (additionalContext?.attachments as FileAttachment[]) ?? [];

  // Feature flag for slash commands
  const isSlashCommandEnabled = useFeatureFlag('nova-slash-command', false);

  // Slash command menu state
  const slashCommandMenu = useSlashCommandMenu();
  const { commands } = useSlashCommands({
    accountUuid: isSlashCommandEnabled ? accountUuid : undefined,
    userUuid: isSlashCommandEnabled ? userUuid : undefined,
  });

  const adjustHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + 2}px`;
    }
  }, [textareaRef]);

  const resetHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = '92px';
    }
  }, [textareaRef]);

  useEffect(() => {
    if (textareaRef.current) {
      adjustHeight();
    }
  }, [adjustHeight]);

  const handleInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setInput(newValue);
    adjustHeight();

    // Handle slash command detection (only when feature flag is enabled)
    if (isSlashCommandEnabled) {
      if (newValue.startsWith('/')) {
        if (!slashCommandMenu.isOpen) {
          slashCommandMenu.open(newValue);
        } else {
          slashCommandMenu.updateQuery(newValue);
        }
      } else if (slashCommandMenu.isOpen) {
        slashCommandMenu.close();
      }
    }
  };

  // Handle slash command selection
  const handleSlashCommandSelect = useCallback(
    (command: { handle: string }) => {
      setInput(`/${command.handle} `);
      slashCommandMenu.close();
      textareaRef.current?.focus();
    },
    [setInput, slashCommandMenu, textareaRef],
  );

  const submitForm = useCallback(() => {
    handleSubmit(undefined);
    resetHeight();
  }, [handleSubmit, resetHeight]);

  return (
    <div className="relative flex w-full flex-col gap-4">
      {isSlashCommandEnabled && (
        <SlashCommandMenu
          isOpen={slashCommandMenu.isOpen}
          onClose={slashCommandMenu.close}
          onSelect={handleSlashCommandSelect}
          commands={commands}
          searchQuery={slashCommandMenu.searchQuery}
          anchorRef={textareaRef}
        />
      )}
      <Textarea
        data-testid="chat-input"
        ref={textareaRef}
        placeholder={placeholder}
        value={input}
        onChange={handleInput}
        onPaste={handlePaste}
        className={cn(
          'max-h-[calc(25dvh)] min-h-[24px] resize-none overflow-y-auto',
          'bg-background shadow-cardShadow !text-base transition-colors',
          'min-h-[92px] border-none focus-within:border-none hover:focus-within:border-none',
          className,
          {
            'rounded-b-[0px] border-b-0': isNotEmpty(attachments),
          },
        )}
        rows={2}
        autoFocus
        onKeyDown={(event) => {
          // Skip form submission if slash command menu is open and Enter is pressed
          if (
            isSlashCommandEnabled &&
            slashCommandMenu.isOpen &&
            (event.key === 'Enter' || event.key === 'Tab' || event.key === 'Escape')
          ) {
            // Let the SlashCommandMenu handle these keys
            return;
          }

          if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
            event.preventDefault();
            if (status === 'ready') {
              submitForm();
            }
          }
        }}
      />
    </div>
  );
}
